import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Input, InputNumber, DatePicker, Select, Radio, Checkbox, Upload, Button, message, } from 'antd';
import { Camera } from 'lucide-react';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useState } from 'react';
import { ChecklistField } from './ChecklistField';
import { DailyScheduleField } from './DailyScheduleField';
import { SignatureField } from './SignatureField';
import { TableField } from './TableField';
const FileFieldUpload = memo(function FileFieldUpload({ field, value, onChange, disabled, uploadHandlers, }) {
    // multiple 时 value 是 number[]，否则是 number | undefined
    const multiple = !!field.multiple;
    const ids = multiple
        ? (value ?? [])
        : value != null
            ? [value]
            : [];
    // 阶段 3：fileId → blob URL 缓存（用于 antd Upload 的预览/下载链接）
    const [urlCache, setUrlCache] = useState({});
    useEffect(() => {
        if (!uploadHandlers?.resolveBlobUrl)
            return;
        let cancelled = false;
        void (async () => {
            const next = {};
            for (const id of ids) {
                if (urlCache[id]) {
                    next[id] = urlCache[id];
                    continue;
                }
                const url = await uploadHandlers.resolveBlobUrl?.(id);
                if (url)
                    next[id] = url;
            }
            if (!cancelled)
                setUrlCache((prev) => ({ ...prev, ...next }));
        })();
        return () => {
            cancelled = true;
        };
        // 依赖 ids 字符串化（数组引用每次 render 都是新的）+ uploadHandlers 引用
    }, [ids.join(','), uploadHandlers]);
    /**
     * customRequest 工厂：拿到 File → uploadHandlers.onUploadFile → 拿到 file id → 写回 onChange。
     */
    const customRequest = useCallback(async ({ file, onSuccess, onError, }) => {
        if (!uploadHandlers) {
            onError?.(new Error('上傳通道未配置'));
            return;
        }
        try {
            const realFile = file;
            const uploaded = await uploadHandlers.onUploadFile(realFile);
            if (multiple) {
                const cur = value ?? [];
                onChange([...cur, uploaded.id]);
            }
            else {
                onChange(uploaded.id);
            }
            onSuccess?.({ id: uploaded.id });
        }
        catch (err) {
            message.error('上傳失敗');
            onError?.(err);
        }
    }, [uploadHandlers, value, onChange, multiple]);
    // 文件名解析：优先 uploadHandlers.fileMetas，命中失败 fallback file#${id}
    // url 来自 urlCache（resolveBlobUrl 解析的 blob: 或 https:）
    const fileList = ids.map((id) => ({
        uid: String(id),
        name: uploadHandlers?.fileMetas?.get(id)?.filename ?? `file#${id}`,
        status: 'done',
        url: urlCache[id],
    }));
    const handleRemove = (file) => {
        const removeId = Number(file.uid);
        if (multiple) {
            const cur = value ?? [];
            onChange(cur.filter((id) => id !== removeId));
        }
        else {
            onChange(null);
        }
        // 通知宿主（用于失效 fileMetas 缓存等）
        uploadHandlers?.onRemoveFile?.(removeId);
        return true;
    };
    return (_jsx(Upload, { disabled: disabled || !uploadHandlers, multiple: multiple, fileList: fileList, customRequest: uploadHandlers
            ? (options) => void customRequest(options)
            : undefined, onRemove: handleRemove, showUploadList: { showRemoveIcon: !disabled, showPreviewIcon: false }, accept: field.type === 'file' && 'accept' in field ? field.accept : undefined, children: _jsx(Button, { icon: field.type === 'photo' ? _jsx(Camera, { size: 14 }) : undefined, disabled: disabled || !uploadHandlers, children: field.type === 'photo' ? '拍照 / 上傳照片' : '上傳檔案' }) }));
});
/**
 * 根據 FormField 類型渲染對應的 antd 輸入控件。
 * 所有控件均為受控組件，由父層管理 value + onChange。
 */
export const FieldFactory = memo(function FieldFactory({ field, value, onChange, disabled, uploadHandlers, }) {
    switch (field.type) {
        case 'text':
            return (_jsx(Input, { value: value ?? '', onChange: (e) => onChange(e.target.value), disabled: disabled, maxLength: field.maxLength, placeholder: field.placeholder }));
        case 'textarea':
            return (_jsx(Input.TextArea, { rows: field.rows ?? 4, value: value ?? '', onChange: (e) => onChange(e.target.value), disabled: disabled, maxLength: field.maxLength }));
        case 'number':
            return (_jsx(InputNumber, { value: value, onChange: (n) => onChange(n), disabled: disabled, min: field.min, max: field.max, step: field.step, addonAfter: field.unit }));
        case 'date':
        case 'datetime':
            return (_jsx(DatePicker, { value: value ? dayjs(value) : null, onChange: (d) => onChange(d?.toISOString() ?? null), showTime: field.type === 'datetime', disabled: disabled }));
        case 'select':
            return (_jsx(Select, { value: value, onChange: (v) => onChange(v), disabled: disabled, options: field.options, allowClear: true, style: { width: '100%' } }));
        case 'multiselect':
            // 序70：display='checkbox' 以勾選框橫排呈現，並可前後夾只讀條文（如驗證依據全文）
            if (field.display === 'checkbox') {
                return (_jsxs("div", { style: { lineHeight: 2 }, children: [field.prefix && _jsx("span", { children: field.prefix }), _jsx(Checkbox.Group, { value: value ?? [], onChange: (v) => onChange(v), disabled: disabled, options: field.options, style: { display: 'inline-flex', flexWrap: 'wrap', verticalAlign: 'middle' } }), field.suffix && _jsx("span", { children: field.suffix })] }));
            }
            return (_jsx(Select, { mode: "multiple", value: value ?? [], onChange: (v) => onChange(v), disabled: disabled, options: field.options, style: { width: '100%' } }));
        case 'radio':
            return (_jsx(Radio.Group, { value: value, onChange: (e) => onChange(e.target.value), disabled: disabled, options: field.options }));
        case 'checkbox':
            return (_jsx(Checkbox, { checked: value, onChange: (e) => onChange(e.target.checked), disabled: disabled, children: field.label }));
        case 'file':
        case 'photo':
            return (_jsx(FileFieldUpload, { field: field, value: value, onChange: onChange, disabled: disabled, uploadHandlers: uploadHandlers }));
        case 'signature':
            return (_jsx(SignatureField, { field: field, value: value, onChange: onChange, disabled: disabled, uploadHandlers: uploadHandlers }));
        case 'table':
            // 可編輯多行表格（行動態增刪；列類型 text / number / date / signature）
            // uploadHandlers 透傳供 signature 列上傳/預覽格內手寫簽名
            return (_jsx(TableField, { field: field, value: value, onChange: onChange, disabled: disabled, uploadHandlers: uploadHandlers }));
        case 'dailySchedule':
            // 多日行程（序74）：多組「現場稽核日期 + 當日行程」，行程列不含日期
            return (_jsx(DailyScheduleField, { field: field, value: value, onChange: onChange, disabled: disabled, uploadHandlers: uploadHandlers }));
        case 'checklist':
            // onChange 直接透传（Renderer 已保证其引用稳定），避免每次渲染新建闭包打穿 ChecklistField 的 memo
            return (_jsx(ChecklistField, { field: field, value: value ?? {}, onChange: onChange, disabled: disabled, uploadHandlers: uploadHandlers }));
    }
});
//# sourceMappingURL=FieldFactory.js.map